'use client';

import {
  type BackgroundColor,
  backgroundToTokens,
} from '@ironhack/design-system2/utils';
import { insert } from 'ramda';
import { type ReactElement, useContext, useState } from 'react';

import { type QuizMatchHeaderData } from '@/app/components/server/quiz-match-header';
import { QuizProgressContext } from '../../quiz-progress-context';
import { CallToAction } from './call-to-action';
import { type CallToActionForm } from './call-to-action/form';
import {
  type CourseAnswer,
  IconQuestion,
  type IconQuestionData,
  type SelectedCourseAnswer,
} from './icon-question';
import { ImageQuestion, type ImageQuestionData } from './image-question';
import { type ImageAnswer } from './image-question/image-answer';
import {
  type ConnectedImageQuestionData,
  convertConnectedToImageQuestion,
} from './image-question/connected-image-question';
import { StartScreen, type StartScreenData } from './start-screen';

type Props = {
  backgroundColor: BackgroundColor;
  callToAction: {
    form: CallToActionForm;
    screen: QuizMatchHeaderData;
  };
  questions: Array<
    ConnectedImageQuestionData | IconQuestionData | ImageQuestionData
  >;
  startScreen: StartScreenData;
};

export const QuizContent = (props: Props): ReactElement => {
  const { backgroundColor, callToAction, questions, startScreen } = props;

  const [currentStep, setCurrentStep] = useContext(QuizProgressContext) ?? [0];
  const [answers, setAnswers] = useState<
    Array<SelectedCourseAnswer | ImageAnswer>
  >([]);

  const { color } = backgroundToTokens(backgroundColor);
  const callToActionStep = questions.length + 1;
  const genderId = answers[0] && 'code' in answers[0] ? answers[0].code : null;

  const goToNextStep = () => setCurrentStep?.(currentStep + 1);

  switch (currentStep) {
    case 0: {
      return (
        <StartScreen color={color} data={startScreen} onClick={goToNextStep} />
      );
    }

    case callToActionStep: {
      return (
        <CallToAction answers={answers} color={color} data={callToAction} />
      );
    }

    default: {
      const questionIndex = currentStep - 1;
      const questionData = questions[questionIndex];
      const saveAnswer = (answer: CourseAnswer | ImageAnswer): void => {
        let stateAnswer = answer as SelectedCourseAnswer | ImageAnswer;
        if ('code' in questionData) {
          const { code } = questionData;
          stateAnswer = { ...answer, questionCode: code };
        }
        const updatedAnswers = insert(questionIndex, stateAnswer, answers);
        setAnswers(updatedAnswers);
        goToNextStep();
      };

      const isImageQuestion =
        questionData.__typename === 'ConnectedImageQuestionRecord' ||
        questionData.__typename === 'ImageQuestionRecord';

      if (isImageQuestion) {
        const data =
          questionData.__typename === 'ConnectedImageQuestionRecord'
            ? convertConnectedToImageQuestion(genderId, questionData)
            : questionData;

        return (
          <ImageQuestion color={color} data={data} onAnswer={saveAnswer} />
        );
      }

      return (
        <IconQuestion color={color} data={questionData} onAnswer={saveAnswer} />
      );
    }
  }
};
